import { DateTime } from 'luxon'
import { useCallback } from 'react'

import { isInputConfig } from '../../../../application/formBuilder/config'
import { usePolicy } from '../../../../application/product/policy/PolicyProvider'
import {
  AssetDisclosureChange,
  Data,
  getCoreAsset,
  getPolicyholder,
  getPolicyVersion,
  Policy,
  PolicyAdjustmentRequest,
  PolicyChangeType,
  PolicyholderDisclosureChange,
  ProfileDisclosureChange,
  RequestCoverOptionChange,
} from '../../../../domain'
import { useAdjustments } from '../../../../state'
import {
  AssetChangeConfig,
  ChangeType,
  FormConfig,
  ProfileChangeConfig,
  QuoteChangeConfig,
  RequestCoverOptionChangeConfig,
} from '../../../../state/configuration'
import { hasUpdates, toUpdates } from '../../helpers'

const toPolicyAssetChange =
  (existingData: Data, data: Data, policy: Policy) =>
  (
    policyAdjustment: PolicyAdjustmentRequest,
    field: string,
    adjustment: AssetChangeConfig,
  ): PolicyAdjustmentRequest => {
    const version = getPolicyVersion(policy)
    const { assetCode } = getCoreAsset(version.quote)

    const updates = toUpdates(existingData, data)

    const changes: AssetDisclosureChange[] = updates(field, adjustment).map((update) => ({
      changeType: PolicyChangeType.ASSET_DISCLOSURE_CHANGE,
      assetCode,
      update,
    }))

    return {
      ...policyAdjustment,
      changes: [...policyAdjustment.changes, ...changes],
    }
  }

const toPolicyProfileReducer =
  (existingData: Data, data: Data, profileCode: string) =>
  (
    policyAdjustment: PolicyAdjustmentRequest,
    field: string,
    adjustment: ProfileChangeConfig,
  ): PolicyAdjustmentRequest => {
    const updates = toUpdates(existingData, data)

    const changes: ProfileDisclosureChange[] = updates(field, adjustment).map(
      (updateWithPolicyholderPath) => {
        const { policyholderPath, ...update } = updateWithPolicyholderPath
        return {
          changeType: PolicyChangeType.PROFILE_DISCLOSURE_CHANGE,
          profileCode,
          update,
          policyholderPath,
        }
      },
    )

    return {
      ...policyAdjustment,
      changes: [...policyAdjustment.changes, ...changes],
    }
  }

const toStartDateReducer =
  (data: Data) =>
  (policyAdjustment: PolicyAdjustmentRequest, field: string): PolicyAdjustmentRequest => {
    return {
      ...policyAdjustment,
      adjustmentType: 'SDA',
      effectiveAt: DateTime.fromISO(data[field]).toISO(),
    }
  }

const toPolicyQuoteReducer =
  (existingData: Data, data: Data) =>
  (
    policyAdjustment: PolicyAdjustmentRequest,
    field: string,
    adjustment: QuoteChangeConfig,
  ): PolicyAdjustmentRequest => {
    const updates = toUpdates(existingData, data)

    const changes: PolicyholderDisclosureChange[] = updates(field, adjustment).map(
      (update) => ({
        changeType: PolicyChangeType.POLICYHOLDER_DISCLOSURE_CHANGE,
        update,
      }),
    )

    return {
      ...policyAdjustment,
      changes: [...policyAdjustment.changes, ...changes],
    }
  }

const toCoverOptionReducer =
  (data: Data) =>
  (
    policyAdjustment: PolicyAdjustmentRequest,
    field: string,
    adjustment: RequestCoverOptionChangeConfig,
  ): PolicyAdjustmentRequest => {
    const change: RequestCoverOptionChange = {
      changeType: PolicyChangeType.REQUEST_COVER_OPTION,
      coverOption: adjustment.coverCode,
      selected: data[field],
    }

    return {
      ...policyAdjustment,
      changes: [...policyAdjustment.changes, change],
    }
  }

export const usePolicyAdjustmentRequest = (form: FormConfig) => {
  const { policy } = usePolicy()
  const { getAdjustment } = useAdjustments()

  return useCallback(
    (
      existingData: Data,
      updatedData: Data,
      effectiveAt?: string,
    ): PolicyAdjustmentRequest => {
      const { quote } = policy.currentPolicyVersion
      const { profileCode } = getPolicyholder(quote)

      const coverOptionReducer = toCoverOptionReducer(updatedData)
      const startDateReducer = toStartDateReducer(updatedData)
      const quoteReducer = toPolicyQuoteReducer(existingData, updatedData)
      const assetReducer = toPolicyAssetChange(existingData, updatedData, policy)
      const profileReducer = toPolicyProfileReducer(
        existingData,
        updatedData,
        profileCode,
      )

      return form
        .filter(isInputConfig)
        .filter(hasUpdates(existingData, updatedData))
        .reduce(
          (policyAdjustment: PolicyAdjustmentRequest, input) => {
            const adjustment = getAdjustment(input.name)

            switch (adjustment.changeType) {
              case ChangeType.PROFILE_DISCLOSURE:
                return profileReducer(policyAdjustment, input.name, adjustment)
              case ChangeType.ASSET_DISCLOSURE:
                return assetReducer(policyAdjustment, input.name, adjustment)
              case ChangeType.POLICYHOLDER_DISCLOSURE:
                return quoteReducer(policyAdjustment, input.name, adjustment)
              case ChangeType.REQUEST_COVER_OPTION:
                return coverOptionReducer(policyAdjustment, input.name, adjustment)
              case ChangeType.START_DATE_CHANGE:
                return startDateReducer(policyAdjustment, input.name)
              default:
                throw new Error('Invalid adjustment type')
            }
          },
          {
            policyId: policy.policyId,
            adjustmentType: 'MTA',
            changes: [],
            ...(effectiveAt && { effectiveAt }),
          },
        )
    },
    [policy, getAdjustment],
  )
}
